import React from 'react';
import { format } from 'date-fns';
import { parseStringDate } from 'helpers/_date-helpers';
import { useDateSettingsFormat } from 'common/useDateSettingsFormat';
import { SECTIONS } from './constants';

const pad = (n) => String(n ?? 0).padStart(2, '0');

const format12h = ({ hour, minute }) => {
  const h = Number(hour);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${pad(h % 12 || 12)}:${pad(minute)} ${ampm}`;
};

// ISO instant → tz-aware wall-clock (browser zone = the recipient's client zone).
const formatInstant = (iso, pattern) => format(new Date(iso), pattern);
const formatRange = ({ start, end }) =>
  `${formatInstant(start, 'hh:mm a MM/dd')} – ${formatInstant(end, 'hh:mm a MM/dd')}`;
const formatTravel = ({ hours, minutes } = {}) => `${Number(hours) || 0}h ${Number(minutes) || 0}m`;

export const formatInitials = (fullName = '') => {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length < 2) return fullName;
  return `${parts[0][0]}. ${parts.slice(1).join(' ')}`;
};

const placeList = (value) =>
  (Array.isArray(value) ? value : []).map((o) => o.name || o.address).filter(Boolean).join(', ');

const SubRow = ({ label, value, classes }) => (
  <div className={classes.subRow}>
    <span className={classes.subLabel}>{label}: </span>
    {value}
  </div>
);

const CrewValue = ({ value, removed, classes }) => {
  const nameLine = (
    <div>
      {formatInitials(value?.fullName) || value?.username}
      {value?.isCrewLeader ? <span className={classes.cl}> (CL)</span> : null}
    </div>
  );

  if (removed) return nameLine;

  const startTimeText = value?.startTimeDate
    ? formatInstant(value.startTimeDate, 'hh:mm a')
    : value?.startTime
    ? format12h(value.startTime)
    : null;

  return (
    <>
      {nameLine}
      {startTimeText ? <SubRow label="Individual Start Time" value={startTimeText} classes={classes} /> : null}
      {(value?.hours || []).map((h, i) => (
        <React.Fragment key={i}>
          {h?.start && h?.end ? (
            <SubRow label="Individual On Job Time" value={formatRange(h)} classes={classes} />
          ) : null}
          {h?.travelTo ? (
            <SubRow label="Individual Time To Job" value={formatTravel(h.travelTo)} classes={classes} />
          ) : null}
          {h?.travelFrom ? (
            <SubRow label="Individual Time From Job" value={formatTravel(h.travelFrom)} classes={classes} />
          ) : null}
        </React.Fragment>
      ))}
    </>
  );
};

const SectionValue = ({ change, classes }) => {
  const { dateFormat } = useDateSettingsFormat();
  const { section, changeType } = change;
  // Edited rows show what the field changed FROM — the worklog itself already shows the new value.
  const value = changeType === 'EDITED' && change.oldValue != null ? change.oldValue : change.value;

  switch (section) {
    case SECTIONS.NOTES:
    case SECTIONS.INTERNAL_NOTES:
    case SECTIONS.WEATHER_NOTES:
      return <div>{value}</div>;

    case SECTIONS.DATE: {
      const parsed = value ? parseStringDate(value) : null;
      return <div>{parsed ? format(parsed, dateFormat) : ''}</div>;
    }

    case SECTIONS.WEATHER:
      return (
        <>
          <SubRow label="Temperature" value={(value?.temperature || []).filter(Boolean).join(', ')} classes={classes} />
          <SubRow label="Humidity" value={value?.humidity} classes={classes} />
        </>
      );

    case SECTIONS.RALLY_POINT:
    case SECTIONS.PROJECT_LOCATION:
      return <div>{placeList(value)}</div>;

    case SECTIONS.COMMENTS:
      return <div>{(value || []).map((c) => c.text).filter(Boolean).join('; ')}</div>;

    case SECTIONS.START_TIME:
      return <div>{value ? format12h(value) : ''}</div>;

    case SECTIONS.ON_JOB_TIME:
      return <div>{value ? formatRange(value) : ''}</div>;

    case SECTIONS.TRAVEL_TIME_TO:
    case SECTIONS.TRAVEL_TIME_FROM:
      return <div>{formatTravel(value)}</div>;

    case SECTIONS.CREW:
      return <CrewValue value={value} removed={changeType === 'REMOVED'} classes={classes} />;

    case SECTIONS.EQUIPMENT: {
      const ranges = (value?.hours || []).map((h) => formatRange(h)).join('\n');
      return (
        <>
          <div>{value?.name}</div>
          {ranges ? <SubRow label="On Job Time" value={ranges} classes={classes} /> : null}
        </>
      );
    }

    default:
      return null;
  }
};

export default SectionValue;
